import type { MutationResult } from '../../api/contract';
import { bangkokWallTimeToRfc3339 } from '../commission-ledger/types';
import type { ReconciliationRepository } from './repository';
import type {
  ReconciliationException,
  ReconciliationExceptionDetail,
  ReconciliationRun,
} from './types';
import { RESOLUTION_REASON_MAX_LENGTH } from './types';

const runA: ReconciliationRun = Object.freeze({
  id: 'run-a',
  type: 'WALLET_BALANCE',
  trigger: 'MANUAL',
  phase: 'COMPLETED',
  partner: { id: 'partner-a', code: 'PARTNER-A', name: 'Partner A' },
  reason: 'Daily wallet control',
  checkedCount: '1200',
  exceptionCount: '1',
  openedExceptionCount: '1',
  resolvedExceptionCount: '0',
  failureCode: null,
  failureAttemptCount: 0,
  requestedAt: '2026-07-23T02:00:00Z',
  processingStartedAt: '2026-07-23T02:00:05Z',
  lastHeartbeatAt: '2026-07-23T02:01:00Z',
  completedAt: '2026-07-23T02:01:10Z',
  requestedBy: { id: 'admin-a', displayName: 'Mock Super Admin' },
  publicRequestId: 'req-run-a',
});

const exceptionA: ReconciliationException = Object.freeze({
  id: 'exception-a',
  runId: 'run-a',
  lastRunId: 'run-a',
  partner: { id: 'partner-a', code: 'PARTNER-A', name: 'Partner A' },
  type: 'AMOUNT_MISMATCH',
  severity: 'CRITICAL',
  entityType: 'TRANSACTION',
  entityId: 'transaction-a',
  expected: {
    amount: '1250.00',
    currency: 'THB',
    difference: '250.00',
    duplicateIds: ['tx-duplicate-a'],
    missingReceipts: ['receipt-missing-a'],
    staleReservations: ['reservation-a'],
    evidence: ['provider-statement-42'],
  },
  actual: { amount: '1000.00', currency: 'USD' },
  status: 'OPEN',
  firstDetectedAt: '2026-07-23T09:00:00+07:00',
  lastDetectedAt: '2026-07-23T10:00:00+07:00',
  occurrenceCount: '2',
  acknowledgement: null,
  resolution: null,
});

export class MockReconciliationRepository implements ReconciliationRepository {
  readonly #runs: ReconciliationRun[] = [runA];
  readonly #exceptions: ReconciliationException[] = [exceptionA];
  readonly #createResults = new Map<string, MutationResult<ReconciliationRun>>();
  readonly #resolveResults = new Map<string, MutationResult<ReconciliationExceptionDetail>>();
  readonly #acknowledgeResults = new Map<string, MutationResult<ReconciliationExceptionDetail>>();

  async listRuns(query: Parameters<ReconciliationRepository['listRuns']>[0], _signal: AbortSignal) {
    const items = this.#runs.filter(
      (run) =>
        (query.filters.type === '' || run.type === query.filters.type) &&
        (query.filters.trigger === '' || run.trigger === query.filters.trigger) &&
        (query.filters.phase === '' || run.phase === query.filters.phase) &&
        (query.filters.partnerId.trim() === '' ||
          run.partner?.id === query.filters.partnerId.trim()) &&
        timestampInRange(run.requestedAt, query.filters.requestedFrom, query.filters.requestedTo),
    );
    return { items, nextCursor: null, requestId: 'mock-req-runs' };
  }

  async createRun(
    input: Parameters<ReconciliationRepository['createRun']>[0],
    idempotencyKey: string,
  ) {
    const prior = this.#createResults.get(idempotencyKey);
    if (prior !== undefined) return prior;
    const reason = requiredReason(input.reason);
    const partnerId = input.partnerId?.trim() ?? '';
    const created: ReconciliationRun = {
      ...runA,
      id: `mock-run-${this.#runs.length + 1}`,
      type: input.type,
      trigger: 'MANUAL',
      phase: 'QUEUED',
      partner: partnerId.length === 0 ? null : { id: partnerId, code: partnerId, name: partnerId },
      reason,
      requestedAt: '2026-07-23T12:00:00+07:00',
      processingStartedAt: null,
      lastHeartbeatAt: null,
      completedAt: null,
      checkedCount: '0',
      exceptionCount: '0',
      openedExceptionCount: '0',
      resolvedExceptionCount: '0',
      publicRequestId: 'mock-req-create-run',
    };
    this.#runs.unshift(created);
    const result = { data: created, requestId: 'mock-req-create-run' };
    this.#createResults.set(idempotencyKey, result);
    return result;
  }

  async getRun(runId: string, _signal: AbortSignal) {
    const run = this.#runs.find((item) => item.id === runId);
    if (run === undefined) throw new Error('Mock reconciliation run not found');
    return run;
  }

  async listExceptions(
    query: Parameters<ReconciliationRepository['listExceptions']>[0],
    _signal: AbortSignal,
  ) {
    const filters = query.filters;
    const items = this.#exceptions.filter(
      (item) =>
        (filters.status === '' || item.status === filters.status) &&
        (filters.severity === '' || item.severity === filters.severity) &&
        (filters.type.trim() === '' || item.type === filters.type.trim().toUpperCase()) &&
        (filters.entityType.trim() === '' ||
          item.entityType === filters.entityType.trim().toUpperCase()) &&
        (filters.entityId.trim() === '' || item.entityId === filters.entityId.trim()) &&
        (filters.partnerId.trim() === '' || item.partner?.id === filters.partnerId.trim()) &&
        timestampInRange(item.lastDetectedAt, filters.detectedFrom, filters.detectedTo),
    );
    return { items, nextCursor: null, requestId: 'mock-req-exceptions' };
  }

  async getException(exceptionId: string, _signal: AbortSignal) {
    const exception = this.#exceptions.find((item) => item.id === exceptionId);
    if (exception === undefined) throw new Error('Mock reconciliation exception not found');
    return exception;
  }

  async resolveException(
    exceptionId: string,
    input: Parameters<ReconciliationRepository['resolveException']>[1],
    idempotencyKey: string,
  ) {
    const prior = this.#resolveResults.get(idempotencyKey);
    if (prior !== undefined) return prior;
    const reason = requiredReason(input.reason);
    const current = this.#exceptions.find((item) => item.id === exceptionId);
    if (current === undefined) throw new Error('Mock reconciliation exception not found');
    const resolved: ReconciliationExceptionDetail = {
      ...current,
      status: 'RESOLVED',
      resolution: {
        method: 'MANUAL',
        resolvedAt: '2026-07-23T12:30:00+07:00',
        resolvedBy: 'admin-a',
        reason,
      },
    };
    const index = this.#exceptions.findIndex((item) => item.id === exceptionId);
    this.#exceptions[index] = resolved;
    const result = { data: resolved, requestId: 'mock-req-resolve' };
    this.#resolveResults.set(idempotencyKey, result);
    return result;
  }

  async acknowledgeException(
    exceptionId: string,
    input: Parameters<ReconciliationRepository['acknowledgeException']>[1],
    idempotencyKey: string,
  ) {
    const prior = this.#acknowledgeResults.get(idempotencyKey);
    if (prior !== undefined) return prior;
    const reason = requiredReason(input.reason);
    const evidenceReference = requiredReason(input.evidenceReference);
    const current = this.#exceptions.find((item) => item.id === exceptionId);
    if (current === undefined) throw new Error('Mock reconciliation exception not found');
    const acknowledged: ReconciliationExceptionDetail = {
      ...current,
      status: 'ACKNOWLEDGED',
      acknowledgement: {
        acknowledgedAt: '2026-07-23T12:30:00+07:00',
        acknowledgedBy: 'admin-a',
        reason,
        evidenceReference,
        evidenceFingerprint: 'a'.repeat(64),
        lastObservedAt: '2026-07-23T12:30:00+07:00',
        observationCount: '1',
      },
    };
    const index = this.#exceptions.findIndex((item) => item.id === exceptionId);
    this.#exceptions[index] = acknowledged;
    const result = { data: acknowledged, requestId: 'mock-req-acknowledge' };
    this.#acknowledgeResults.set(idempotencyKey, result);
    return result;
  }
}

function requiredReason(value: string): string {
  const reason = value.trim();
  if (reason.length === 0) throw new Error('A reason is required');
  if (reason.length > RESOLUTION_REASON_MAX_LENGTH)
    throw new Error('Reason must be 2,000 characters or fewer');
  return reason;
}

function timestampInRange(timestamp: string, from: string, to: string): boolean {
  const instant = Date.parse(timestamp);
  const lower = filterInstant(from);
  const upper = filterInstant(to);
  return (lower === null || instant >= lower) && (upper === null || instant < upper);
}

function filterInstant(value: string): number | null {
  if (value.trim().length === 0) return null;
  return Date.parse(bangkokWallTimeToRfc3339(value));
}
